import ActionMenu from "Components/Common/ActionMenu";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { IconName } from "Components/Icons/Icon";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader, Col, Table } from "reactstrap";
import {
  clearInvoiceDetails,
  resetCreateInvoiceStatus,
  resetEditInvoiceStatus,
} from "slices/invoices/reducer";
import { clearItemState } from "slices/items/reducer";
import { clearQuoteDetails } from "slices/quotes/reducer";
import { deleteInvoice, deleteQuote, getDashboard } from "slices/thunks";
import { ClientRecentActivity } from "types/client.api";
import { DashboardApiPayload } from "types/dashboard.api";
import { formatDate_DD_MMM_YYYY } from "utils/formatDates";
import { getAvatarStyle, getInitials } from "utils/getAvatarColor";
import { getStatusBadge } from "utils/getStatusBatch";
import { ErrorToast } from "utils/toast";

interface RecentActivityProps {
  data: DashboardApiPayload;
}

const RecentActivity: React.FC<RecentActivityProps> = ({ data }) => {
  const [deleteModal, setDeleteModal] = useState<{
    type: "quote" | "invoice" | "client";
    id: number;
  } | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showSubscriptionExpiredPopUp, setShowSubscriptionExpiredPopUp] =
    useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const hasNotSubscribed =
    user?.is_subscription_active === false && user.is_trial_period === false;
  const hasBankAccount = user?.hasBankAccountDetailAdded;
  const hasSignature = user?.hasSignatureAdded;
  const [restrictionModalData, setRestrictionModalData] = useState<{
    iconName: IconName;
    title: string;
    description: string;
    buttonText: string;
    onAction: () => void;
  }>({
    iconName: "" as IconName,
    title: "",
    description: "",
    buttonText: "",
    onAction: () => {},
  });
  const RESTRICTION_CONFIG = {
    SUBSCRIPTION_REQUIRED: {
      iconName: "SubscriptionRequired",
      title: "Subscription Required",
      description:
        "To continue using EaziQuote, please activate or renew your subscription.",
      buttonText: "Activate Subscription",
      onAction: () => {
        if (user?.is_team_member) {
          ErrorToast(
            "Only the account owner can manage subscriptions. Please contact your owner."
          );
        } else {
          navigate("/subscribe-plan");
        }
      },
    },
    BANK_ACCOUNT_REQUIRED: {
      iconName: "Info",
      title: "Add Bank Details",
      description:
        "Including bank details makes it easier for clients to pay you without follow-ups.",
      buttonText: "Add Bank Details",
      onAction: () => navigate("/payments-and-invoicing"),
    },
    SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Add Your Signature",
      description:
        "Adding a signature helps build trust and makes your quote feel complete and professional.",
      buttonText: "Add Signature",
      onAction: () =>
        navigate("/payments-and-invoicing", {
          state: {
            signatureMissing: true,
          },
        }),
    },
    BANK_AND_SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Complete Your Setup",
      description:
        "Add your bank details and signature to ensure your quotes look professional and include payment information.",
      buttonText: "Complete Setup",
      onAction: () => navigate("/payments-and-invoicing"),
    },
  } as const;

  const goToReceiptDetails = (data: ClientRecentActivity) => {
    if (data.type === "quote") {
      navigate("/quote-details", { state: { id: data.id } });
    }
    if (data.type === "invoice") {
      navigate("/invoice-details", { state: { id: data.id } });
    }
  };

  const onDelete = () => {
    if (deleteModal?.type === "quote") {
      dispatch(deleteQuote(deleteModal.id));
    } else if (deleteModal?.type === "invoice") {
      dispatch(deleteInvoice(deleteModal.id));
    }
    dispatch(getDashboard());
    setDeleteModal(null);
  };

  return (
    <div>
      <DeleteItemModal
        show={deleteModal !== null}
        onDeleteClick={onDelete}
        onCloseClick={() => setDeleteModal(null)}
      />

      <Col lg={12}>
        <Card className="br-10 ">
          <CardHeader className="align-items-center d-flex br-10">
            <p className="text-black2D fw-medium fs-16 font-primary">
              Recent Activity
            </p>
          </CardHeader>

          <CardBody className="pt-0">
            <div className="table-responsive table-card">
              <Table className="table table-borderless table-centered align-middle table-nowrap mb-0">
                <thead className="text-muted bg-greyF5">
                  <tr>
                    <th
                      scope="col"
                      className="text-grey89 fw-medium fs-14 font-primary text-uppercase"
                    >
                      Title
                    </th>
                    <th
                      scope="col"
                      className="text-grey89 fw-medium fs-14 font-primary text-uppercase  "
                    >
                      Quote/Invoice
                    </th>
                    <th
                      scope="col"
                      className="text-grey89 fw-medium fs-14 font-primary text-uppercase"
                    >
                      Client
                    </th>
                    <th
                      scope="col"
                      className="text-grey89 fw-medium fs-14 font-primary text-uppercase"
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span>Amount</span>
                        {/* <div className="arrow-btns">
                          <i className="ri-arrow-up-s-fill up-arrow"></i>
                          <i className="ri-arrow-down-s-fill down-arrow"></i>
                        </div> */}
                      </div>
                    </th>

                    <th
                      scope="col"
                      className="text-grey89 fw-medium fs-14 font-primary text-uppercase"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="text-grey89 fw-medium fs-14 font-primary text-uppercase"
                    >
                      Creation Date
                    </th>
                    <th
                      scope="col"
                      className="text-grey89 fw-medium fs-14 font-primary text-uppercase"
                    >
                      Expiry/Due Date
                    </th>
                    <th
                      scope="col"
                      className="text-grey89 fw-medium fs-14 font-primary text-uppercase"
                    >
                      ACTION
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {data?.recentActivities.map((item) => (
                    <tr key={item.id}>
                      {/* CODE */}
                      <td className="text-black2D fw-normal fs-14 ">
                        {item?.title}
                      </td>

                      {/* TITLE */}
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="flex-grow-1 text-black2D fw-normal fs-14 font-primary">
                            {item?.reference_number}
                          </div>
                        </div>
                      </td>

                      {/* CLIENT */}
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div
                              className="avatar-circle"
                              style={getAvatarStyle(item?.name)}
                            >
                              {getInitials(item?.name)}
                            </div>

                            <div className="text-black2D fw-normal fs-14 font-primary">
                              {item?.name}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* AMOUNT */}
                      <td>
                        <span className="text-black2D fw-normal fs-14 font-primary">
                          £{item?.price.toFixed(2)}
                        </span>
                      </td>

                      {/* STATUS */}
                      <td>
                        <span
                        // className={
                        //   "badge bg-" +
                        //   item.statusColor +
                        //   "-subtle text-" +
                        //   item.statusColor
                        // }
                        >
                          {getStatusBadge(item?.status)}
                        </span>
                      </td>

                      {/* CREATION DATE */}
                      <td className="text-black2D fw-normal fs-14 font-primary">
                        {formatDate_DD_MMM_YYYY(item?.created_at)}
                      </td>

                      {/* EXPIRY DATE */}
                      <td className="text-black2D fw-normal fs-14 font-primary">
                        {formatDate_DD_MMM_YYYY(item?.expiry_date)}
                      </td>

                      {/* ACTION DROPDOWN */}
                      <td>
                        <ActionMenu
                          isEdit={item.is_editable}
                          isDelete={item.is_editable}
                          isView
                          onView={() => {
                            goToReceiptDetails(item);
                          }}
                          onDelete={() => {
                            setDeleteModal({
                              type: item?.type,
                              id: item?.id,
                            });
                          }}
                          onEdit={() => {
                            if (hasNotSubscribed) {
                              setShowSubscriptionExpiredPopUp(true);
                              setRestrictionModalData(
                                RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED
                              );
                            } else if (!hasBankAccount && !hasSignature) {
                              setShowSubscriptionExpiredPopUp(true);
                              setRestrictionModalData(
                                RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED
                              );
                            } else if (!hasBankAccount) {
                              setShowSubscriptionExpiredPopUp(true);
                              setRestrictionModalData(
                                RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED
                              );
                            } else if (!hasSignature) {
                              setShowSubscriptionExpiredPopUp(true);
                              setRestrictionModalData(
                                RESTRICTION_CONFIG.SIGNATURE_REQUIRED
                              );
                            } else {
                              if (item.type === "quote") {
                                dispatch(clearQuoteDetails());
                                dispatch(clearItemState());
                                navigate("/manage-quotes", {
                                  state: { id: item.id },
                                });
                              } else {
                                dispatch(clearQuoteDetails());
                                dispatch(clearInvoiceDetails());
                                dispatch(resetEditInvoiceStatus());
                                dispatch(resetCreateInvoiceStatus());
                                navigate("/manage-invoice", {
                                  state: { id: item.id },
                                });
                              }
                            }
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      </Col>
      <RestrictedAccessModal
        isOpen={showSubscriptionExpiredPopUp}
        onCancel={() => setShowSubscriptionExpiredPopUp(false)}
        iconName={restrictionModalData.iconName}
        title={restrictionModalData.title}
        description={restrictionModalData.description}
        buttonText={restrictionModalData.buttonText}
        onButtonClick={restrictionModalData.onAction}
      />
    </div>
  );
};

export default React.memo(RecentActivity);
